import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../api/axios';
import StatusBadge from '../components/StatusBadge';
import TagBadge from '../components/TagBadge';
import ConfirmDialog from '../components/ConfirmDialog';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import {
  HiOutlineArrowLeft,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineCalendar,
  HiOutlineTag,
  HiOutlinePencil,
  HiOutlineCheckCircle,
  HiOutlineChatAlt2,
  HiOutlineTrash,
  HiOutlineClock
} from 'react-icons/hi';

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newRemark, setNewRemark] = useState('');
  const [remarkLoading, setRemarkLoading] = useState(false);
  const [convertConfirm, setConvertConfirm] = useState(false);

  // Status update
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Follow-up
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpUpdating, setFollowUpUpdating] = useState(false);

  useEffect(() => {
    fetchLead();
  }, [id]);

  const fetchLead = async () => {
    try {
      const res = await API.get(`/leads/${id}`);
      setLead(res.data.data);
      setFollowUpDate(res.data.data.follow_up_date ? res.data.data.follow_up_date.split('T')[0] : '');
    } catch (error) {
      toast.error('Lead not found');
      navigate('/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusUpdating(true);
    try {
      await API.put(`/leads/${id}/status`, { status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
      fetchLead();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleFollowUpUpdate = async () => {
    setFollowUpUpdating(true);
    try {
      await API.put(`/leads/${id}/follow-up`, { follow_up_date: followUpDate || null });
      toast.success(followUpDate ? 'Follow-up date set' : 'Follow-up removed');
      fetchLead();
    } catch (error) {
      toast.error('Failed to update follow-up');
    } finally {
      setFollowUpUpdating(false);
    }
  };

  const handleFollowUpClear = async () => {
    setFollowUpUpdating(true);
    try {
      await API.put('/leads/' + id + '/follow-up', {
        follow_up_date: null,
      });
      setFollowUpDate('');
      toast.success('Follow-up date removed');
      fetchLead();
    } catch (error) {
      toast.error('Failed to clear follow-up');
    } finally {
      setFollowUpUpdating(false);
    }
  };

  const handleAddRemark = async (e) => {
    e.preventDefault();
    if (!newRemark.trim()) return;

    setRemarkLoading(true);
    try {
      await API.post(`/remarks/${id}`, { content: newRemark.trim() });
      toast.success('Remark added');
      setNewRemark('');
      fetchLead();
    } catch (error) {
      toast.error('Failed to add remark');
    } finally {
      setRemarkLoading(false);
    }
  };

  const handleDeleteRemark = async (remarkId) => {
    try {
      await API.delete(`/remarks/${remarkId}`);
      toast.success('Remark deleted');
      fetchLead();
    } catch (error) {
      toast.error('Failed to delete remark');
    }
  };

  const handleConvert = async () => {
    try {
      await API.post(`/leads/${id}/convert`);
      toast.success('Lead converted to customer!');
      fetchLead();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Conversion failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Back Button + Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/leads')}
            className="mt-1 p-2 rounded-lg hover:bg-gray-100 transition text-gray-400 hover:text-gray-600"
          >
            <HiOutlineArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{lead.name}</h1>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <StatusBadge status={lead.status} />
              {(lead.tags || []).map((tag, i) => (
                <TagBadge key={i} tag={tag} />
              ))}
            </div>
          </div>
        </div>

        {lead.status !== 'Converted' && (
          <button
            onClick={() => setConvertConfirm(true)}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
          >
            <HiOutlineCheckCircle className="w-4 h-4" />
            Mark as Converted
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Lead Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Info */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <HiOutlinePhone className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="text-sm font-medium text-gray-900">{lead.phone || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <HiOutlineMail className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{lead.email || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <HiOutlineTag className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Source</p>
                  <p className="text-sm font-medium text-gray-900">{lead.source}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <HiOutlineClock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p className="text-sm font-medium text-gray-900">
                    {format(new Date(lead.created_at), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <HiOutlineChatAlt2 className="w-5 h-5" />
              Remarks / Notes
            </h2>

            {/* Add Remark Form */}
            <form onSubmit={handleAddRemark} className="mb-4">
              <div className="flex gap-2">
                <textarea
                  value={newRemark}
                  onChange={(e) => setNewRemark(e.target.value)}
                  placeholder="Add a remark or note..."
                  className="input-field resize-none"
                  rows={2}
                />
              </div>
              <div className="flex justify-end mt-2">
                <button type="submit" disabled={remarkLoading || !newRemark.trim()} className="btn-primary text-sm">
                  {remarkLoading ? 'Adding...' : 'Add Remark'}
                </button>
              </div>
            </form>

            {/* Remarks List */}
            {lead.remarks && lead.remarks.length > 0 ? (
              <div className="space-y-3">
                {lead.remarks.map((remark) => (
                  <div key={remark.id} className="p-3 bg-gray-50 rounded-lg border border-gray-100 group">
                    <div className="flex items-start justify-between">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap flex-1">{remark.content}</p>
                      <button
                        onClick={() => handleDeleteRemark(remark.id)}
                        className="p-1 rounded text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                      >
                        <HiOutlineTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      {format(new Date(remark.created_at), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">No remarks yet.</p>
            )}
          </div>
        </div>

        {/* Right Column - Status & Follow-up */}
        <div className="space-y-6">
          {/* Status */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Lead Status</h3>
            <div className="space-y-2">
              {['New', 'Contacted', 'Converted', 'Lost'].map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  disabled={statusUpdating || lead.status === status}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${lead.status === status
                    ? 'bg-primary-50 text-primary-700 border-2 border-primary-300'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                >
                  {status === lead.status && '✓ '}{status}
                </button>
              ))}
            </div>
          </div>

          {/* Follow-up */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <HiOutlineCalendar className="w-4 h-4" />
              Follow-up Date
            </h3>
            <input
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              className="input-field mb-3"
            />
            <div className="flex gap-2">
              <button
                onClick={handleFollowUpUpdate}
                disabled={followUpUpdating}
                className="btn-primary text-sm flex-1"
              >
                {followUpUpdating ? 'Saving...' : 'Set Follow-up'}
              </button>
              {followUpDate && (
                <button
                  onClick={handleFollowUpClear}
                  disabled={followUpUpdating}
                  className="btn-secondary text-sm"
                >
                  Clear
                </button>
              )}
            </div>
            {lead.follow_up_date && (
              <p className="text-xs text-orange-600 mt-2 font-medium">
                📅 Follow-up: {format(new Date(lead.follow_up_date), 'MMMM d, yyyy')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Convert Confirm */}
      <ConfirmDialog
        isOpen={convertConfirm}
        onClose={() => setConvertConfirm(false)}
        onConfirm={handleConvert}
        title="Convert to Customer"
        message={`Convert "${lead.name}" to a customer? This will copy data to the Customers module and change status to Converted.`}
        confirmText="Convert"
      />
    </div>
  );
};

export default LeadDetail;